import React, { useCallback, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import NativeModal from 'react-modal';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import async from '@xanadu/ui-shared/components/asyncComponent';
import MarketRules from '@xanadu/ui-shared/components/Modals/MarketRules';
import SwitcherModal from '@xanadu/ui-shared/components/Modals/SwitcherModal';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import MarketingPreferencesModal from '@xanadu/ui-shared/components/Modals/MarketingPreferencesModal';
import { MOBILE_BREAKPOINT } from '@xanadu/ui-shared/constants';
import Login from '../../containers/Login/Login';
import { SignUp, Feedback } from '../../containers/SignUp';
import ResetPassword from '../../containers/ResetPassword';
import IconClose from '../../../images/icons/misc/close.svg';
import KycMessaging from '../../containers/KycMessaging';
import UploadDocuments from '../../containers/UploadDocuments/UploadDocuments';
import OccupationSalaryRange from '../../containers/UploadDocuments/OccupationSalaryRange';
import UploadDocumentsSuccess from '../../containers/UploadDocuments/UploadDocumentsSuccess';
import ResponsibleGamblingInteraction from '../../containers/ResponsibleGamblingInteraction';
import Currency from '../../containers/Currency';
import TermsAndConditionsModal from '../../containers/TermsAndConditionsModal';
import NetDepositLimitModalContent from '../netDepositLimit';
import ZeroPresentationModalContent from '../../containers/ZeroPresentationModalContent';
import CasinoRealityCheckModalContent from '../../containers/Casino/CasinoRealityCheckModalContent';
import BonusInfoModal from '../../containers/BonusInfoModal/BonusInfoModal';
import PreviousLoggedIn from '../../containers/PreviousLoggedIn/PreviousLoggedIn';
import ClientRedirect from '../ClientRedirect/ClientRedirect';
import DepositWithdrawalCheck from '../../containers/DepositWithdrawalCheck/DepositWithdrawalCheck';
import ReviewAccountActivity from '../../containers/ReviewAccountActivity';
import GraiDualVerification from '../KycMessaging/components/GraiDualVerification/GraiDualVerification';

import './Modal.scss';

const AsyncCashier = async(() => import('../../containers/Cashier').then(module => module.default));
const AsyncNewFreeBetsPopup = async(() => import('../../containers/promotions/NewFreeBetsPopup').then(module => module.default));
const AsyncCasinoPromoPopup = async(() => import('../../containers/promotions/CasinoPromoPopup').then(module => module.default));

NativeModal.setAppElement(document.getElementById('root'));

const Modal = ({
  config,
  displayModal,
  setGlobalBannerMessage,
  loginRequest,
  history,
  widthSize,
  userStatus,
}) => {
  const {
    extraData,
    section,
    hasDepositPromptMessage,
    smallModal,
    show,
    email,
    userRegistered,
    showCloseIcon,
    theme,
    redirectOnClose,
    position,
  } = config || {};

  const prevShow = useRef(show);

  const userIsLocked = userStatus === 'locked';

  const handlePageScroll = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    return false;
  }, []);

  const closeModal = () => {
    // TODO: read previous history entry to redirect to that if it's inside matchbook
    if (redirectOnClose) {
      history.push(redirectOnClose);
    }

    displayModal(false);
  };

  const handleOnCloseModal = () => {
    closeModal();

    if (section === 'kyc-documents-warning') {
      track('Registration', 'Upload Docs - Drop Off', { reason: 'Close button' });
    }
  };

  useEffect(() => () => { window.removeEventListener('scroll', handlePageScroll); }, []);

  useEffect(() => {
    if (!prevShow.current && show) {
      window.addEventListener('scroll', handlePageScroll);
    }

    if (prevShow.current && !show) {
      window.removeEventListener('scroll', handlePageScroll);
    }

    prevShow.current = show;
  }, [show]);

  let content;
  let title;
  let extraClasses = `mb-modal--${section} `; // space needed

  if (theme === 'dark') {
    extraClasses += 'mb-modal--streaming ';
  }

  switch (section) {
    case 'login':
      content = (
        <Login
          onRequestClose={closeModal}
          redirectOnClose={redirectOnClose}
        />
      );

      if (userIsLocked) {
        extraClasses += 'mb-modal--noRightSection';
      }
      break;
    case 'registration':
      title = 'Join Matchbook';
      content = (
        <SignUp
          onRequestClose={closeModal}
          key="mb-modal-registration"
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'update-password':
      title = 'Reset Password';
      content = (
        <ResetPassword onRequestClose={closeModal} />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'deposit':
      content = (
        <AsyncCashier
          onRequestClose={closeModal}
          key="mb-modal-deposit"
          section={section}
          hasDepositPromptMessage={hasDepositPromptMessage}
        />
      );

      break;
    case 'withdraw':
      content = (
        <AsyncCashier
          onRequestClose={closeModal}
          key="mb-modal-withdraw"
          section={section}
        />
      );
      break;
    case 'deposit-withdrawal-check':
      title = '';
      content = (
        <DepositWithdrawalCheck
          onRequestClose={closeModal}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'new-free-bets':
      title = '';
      content = (
        <AsyncNewFreeBetsPopup
          onRequestClose={closeModal}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'welcome-casino-promotion':
      title = '';
      content = (
        <AsyncCasinoPromoPopup
          onRequestClose={closeModal}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'bonus-trigger-by-deposit':
      title = '';
      content = (
        <BonusInfoModal
          onRequestClose={closeModal}
          bonus={extraData.get('bonusToShow')}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'previous-logged-in':
      title = '';
      content = (
        <PreviousLoggedIn
          onRequestClose={closeModal}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'switcher':
      title = '';
      content = (
        <SwitcherModal
          onRequestClose={closeModal}
        />
      );
      break;
    case 'market-rules':
      title = '';
      content = (
        <MarketRules
          marketId={extraData.get('marketId')}
        />
      );
      break;
    case 'marketing-preferences':
      title = '';
      content = (
        <MarketingPreferencesModal />
      );
      extraClasses += !showCloseIcon && 'mb-modal--no-header';
      break;
    case 'session-timeout':
      title = '';
      content = (
        <div className="mb-modal__containerSessionTimeout">
          <div className="mb-modal__sessionTimeoutTop">
            <div className="mb-modal__sessionTimeoutLogo" />
            <div className="mb-modal__sessionTimeoutMessage">
              To help keep your account secure we maintain a session expiry policy.
            </div>
            <div className="mb-modal__sessionTimeoutMessage mb-modal__sessionTimeoutMessage--bold">
              Please login to renew your session.
            </div>
          </div>
          <div className="mb-modal__containerEventNotFoundMessageBottom">
            <div className="mb-modal__eventNotFoundMessageButton">
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="primary"
                onClick={() => {
                  closeModal();
                  setTimeout(() => {
                    loginRequest();
                  }, 250);
                }}
              >Yes, log in</Button>
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="flat"
                onClick={closeModal}
                dataHook="no-thanks"
              >no thanks</Button>
            </div>
          </div>
        </div>
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'review-account-activity':
      title = '';
      content = (
        <ReviewAccountActivity
          onRequestClose={closeModal}
        />
      );
      break;
    case 'banned-country':
      title = '';
      content = (
        <div className="mb-modal__containerBannedCountry">
          <div className="mb-modal__bannedCountryTop">
            <div className="mb-modal__bannedCountryMessage">
              <div>Matchbook does not accept customers from your jurisdiction.</div>
              {'We will continue to monitor this as local laws and licensing requirements change in the future.'}
              <br />
              <br />
              {'If you have funds on your account please go to the cashier and process a withdrawal from your account.'}
            </div>
          </div>
          <div className="mb-modal__containerBannedCountryBottom">
            <div className="mb-modal__bannedCountryButton">
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="primary"
                onClick={() => {
                  closeModal();
                  setGlobalBannerMessage({
                    behaviour: 'persistent',
                    bgColor: 'dc901f',
                    message: 'Matchbook does not accept customers from your jurisdiction. We will continue to monitor this as local laws and licensing requirements change in the future. If you have funds on your account please go to the cashier and process a withdrawal from your account.'
                  });
                }}
                dataHook="no-thanks"
              >Acknowledge</Button>
            </div>
          </div>
        </div>
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'events-not-available':
      title = '';
      content = (
        <div className="mb-modal__containerEventNotFoundMessage">
          <div className="mb-modal__containerEventNotFoundMessageTop">
            <div className="mb-modal__eventNotFoundMessageLogo" />
            <div className="mb-modal__eventNotFoundMessage">
              The event or competition you were looking for is not currently available.
            </div>
            <div className="mb-modal__eventNotFoundMessage mb-modal__eventNotFoundMessage--bold">
              Here are some other events that may be of interest to you.
            </div>
          </div>
          <div className="mb-modal__containerEventNotFoundMessageBottom">
            <div className="mb-modal__eventNotFoundMessageButton">
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="primary"
                onClick={closeModal}
              >ok</Button>
            </div>
          </div>
        </div>
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'bonus-claimed':
      title = '';
      content = (
        <div className="mb-modal__containerBonusClaimedMessage">
          <div className="mb-modal__containerBonusClaimedMessageTop">
            <div className="mb-modal__bonusClaimedMessageLogo" />
            <div className="mb-modal__bonusClaimedMessage">
              You currently have an active bonus.
            </div>
            <div className="mb-modal__bonusClaimedMessage mb-modal__bonusClaimedMessage--bold">
              When you have played through this bonus, you will be able to choose another.
            </div>
          </div>
          <div className="mb-modal__containerBonusClaimedMessageBottom">
            <div className="mb-modal__bonusClaimedMessageButton">
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="primary"
                onClick={closeModal}
              >ok</Button>
            </div>
          </div>
        </div>
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'registration-feedback':
      title = '';
      content = (
        <Feedback
          closeModal={closeModal}
          email={email}
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'rg-interaction':
      title = '';
      content = (
        <ResponsibleGamblingInteraction
          closeModal={closeModal}
        />
      );
      break;
    case 'kyc-messaging':
      title = '';
      content = (
        <KycMessaging
          closeModal={closeModal}
        />
      );
      break;
    case 'kyc-documents-warning':
      title = '';
      content = (
        <KycMessaging
          signUpSuccess
          closeModal={closeModal}
        />
      );
      break;
    case 'grai-verification':
      title = '';
      content = (
        <GraiDualVerification displayModal={displayModal} />
      );
      break;
    case 'upload-documents':
      title = '';
      content = (
        <UploadDocuments
          closeModal={closeModal}
          userRegistered={userRegistered}
          documentType={extraData.get('documentType')}
          journeyType={extraData.get('journeyType')}
        />
      );
      break;
    case 'occupation-and-salary-range':
      title = '';
      content = (
        <OccupationSalaryRange
          closeModal={closeModal}
        />
      );
      extraClasses += !showCloseIcon && 'mb-modal--no-header';
      break;
    case 'upload-success':
      title = '';
      content = (
        <UploadDocumentsSuccess
          closeModal={closeModal}
          userRegistered={userRegistered}
        />
      );
      break;
    case 'min-bet-warning':
      title = '';
      content = (
        <div className="mb-modal__containerMinBetWarning">
          <div className="mb-modal__containerMinBetWarningTop">
            <div className="mb-modal__minBetHeader">
              Minimum Bet Required
            </div>
            <div className="mb-modal__minBetMessage">
              {/* eslint-disable-next-line max-len */}
              A minimum bet of <Currency value={2} /> on this race is required to unlock the video stream.
            </div>
          </div>
          <div className="mb-modal__containerMinBetWarningBottom">
            <div className="mb-modal__minBetButton">
              <Button
                disabled={false}
                sizeType="wider"
                buttonType="primary"
                onClick={closeModal}
              >OK</Button>
            </div>
          </div>
        </div>
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'terms-and-conditions':
      content = (
        <TermsAndConditionsModal
          closeModal={closeModal}
          key="mb-modal-terms-and-conditions"
        />
      );
      extraClasses += 'mb-modal--no-header';
      break;
    case 'deposit-allowance':
      title = '';
      content = (
        <NetDepositLimitModalContent
          closeModal={closeModal}
          remaining={extraData.get('remaining')}
          limit={extraData.get('limit')}
        />
      );
      break;
    case 'zero-presentation':
      content = (
        <ZeroPresentationModalContent
          widthSize={widthSize}
        />
      );
      break;
    case 'casino-reality-check':
      title = '';
      content = (
        <CasinoRealityCheckModalContent
          closeModal={closeModal}
          gameId={extraData.get('gameId')}
          widthSize={widthSize}
          history={history}
        />
      );
      break;
    case 'client-redirect':
      title = '';
      content = (
        <ClientRedirect extraData={extraData} />
      );
      break;
    default:
      title = 'Log in to Matchbook';
      content = (
        <div
          className="mb-modal-empty"
          key="mb-modal-empty"
        />
      );
  }

  let smallModalClass = '';
  if (smallModal) {
    smallModalClass = 'mb-modal--smallModal';
  }

  let headerClasses = 'mb-modal__header';
  let headerButtonClasses = 'mb-modal__header__close';
  let headerIconClasses = 'mb-modal__header__icon';
  let contentClasses = `mb-modal__content ${['previous-logged-in', 'deposit-withdrawal-check'].includes(section) ? 'mb-modal__content--noBackground' : null} ${theme === 'dark' ? 'mb-modal__content--dark' : null}`;

  if (!title && showCloseIcon) {
    headerClasses = 'mb-modal__header mb-modal__header--closeOnly';
    headerButtonClasses = `mb-modal__header__close mb-modal__header__close--closeOnly ${['deposit', 'withdraw'].includes(section) && 'mb-modal__header__close--closeOnly--transaction'}`;
    headerIconClasses = 'mb-modal__header__icon mb-modal__header__icon--closeOnly';
    contentClasses += ' mb-modal__content--closeOnly';
  }

  let closeModalIcon = null;
  if (showCloseIcon) {
    closeModalIcon = (
      <div
        role="button"
        tabIndex={0}
        className={headerButtonClasses}
        onClick={handleOnCloseModal}
        data-hook="dialogboxclose"
      >
        <IconClose
          className={headerIconClasses}
        />
      </div>
    );
  }

  const reactModalClass = classNames({
    small: smallModal,
    'react-modal': true,
    'react-modal--center': widthSize > MOBILE_BREAKPOINT || position === 'center',
    'react-modal--bottom': position === 'bottom',
    'react-modal--registration': section === 'registration',
    'react-modal--casino-promotion': section === 'welcome-casino-promotion',
  });

  const reactModalOverlayClass = classNames({
    'mb-modal__overlay': true,
    'mb-modal__overlay--hideBackground': userIsLocked,
  });

  return (
    <NativeModal
      isOpen={show}
      contentLabel="Mb Modal"
      className={reactModalClass}
      overlayClassName={reactModalOverlayClass}
      shouldCloseOnOverlayClick
    >
      <div
        className={`mb-modal ${extraClasses} ${smallModalClass}`}
      >
        {section !== 'rg-interaction' && section !== 'casino-reality-check' ?
          <div className={headerClasses}>
            <h4 data-hook="loginmatchbooktext">
              <span>{title}</span>
            </h4>
            {closeModalIcon}
          </div>
          : null
        }
        <div className={contentClasses}>
          {content}
        </div>
      </div>
    </NativeModal>
  );
};

Modal.propTypes = {
  config: PropTypes.instanceOf(Object).isRequired,
  displayModal: PropTypes.func.isRequired,
  setGlobalBannerMessage: PropTypes.func.isRequired,
  loginRequest: PropTypes.func.isRequired,
  userStatus: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  widthSize: PropTypes.number.isRequired,
};

export default withRouter(Modal);
